import { useLazyQuery } from "@apollo/client";
import ContentCopy from "@mui/icons-material/ContentCopy";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Search from "@mui/icons-material/Search";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link as MuiLink,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { LookupRecordByUuidQuery } from "generated-graphql/graphql";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { validate as isValidUuid } from "uuid";
import {
  LOAD_MORE_AUDIT_ENTRIES,
  LOOKUP_RECORD_BY_UUID,
} from "../../queries/lookupRecordByUuid";
import { useRegisterCommands, type Command } from "../CommandPaletteRegistry";
import { getNavigationLinks } from "./gotoUuid";

type LookupResult = NonNullable<LookupRecordByUuidQuery["lookupRecordByUuid"]>;

const GOTO_UUID_HISTORY_KEY = "goto-uuid-history";
const MAX_HISTORY_ENTRIES = 10;

type HistoryEntry = {
  id: string;
  tableName: string;
  recordName: string | null;
  tenantName: string | null;
  facilityName: string | null;
  searchedAt: number;
};

function getSearchHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(GOTO_UUID_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

function saveToSearchHistory(entry: HistoryEntry): void {
  try {
    let history = getSearchHistory();
    history = history.filter((h) => h.id !== entry.id);
    history.unshift(entry);
    if (history.length > MAX_HISTORY_ENTRIES) {
      history = history.slice(0, MAX_HISTORY_ENTRIES);
    }
    localStorage.setItem(GOTO_UUID_HISTORY_KEY, JSON.stringify(history));
  } catch {
    // Silently fail on storage errors
  }
}

const jsonPreStyles = {
  mt: 1,
  p: 2,
  borderRadius: 1,
  overflow: "auto",
  maxHeight: 400,
  fontSize: "0.75rem",
  fontFamily: "monospace",
  whiteSpace: "pre-wrap",
  wordBreak: "break-all",
  m: 0,
} as const;

function JsonBlock({ data, bgcolor }: { data: unknown; bgcolor: string }) {
  return (
    <Box component="pre" sx={{ ...jsonPreStyles, bgcolor }}>
      {JSON.stringify(data, null, 2)}
    </Box>
  );
}

function formatTimestamp(ts: string): string {
  return new Date(ts).toLocaleString();
}

const OPERATION_COLORS: Record<
  string,
  "success" | "info" | "error" | "warning"
> = {
  INSERT: "success",
  UPDATE: "info",
  DELETE: "error",
  TRUNCATE: "warning",
};

type FieldDiff = {
  field: string;
  oldValue: unknown;
  newValue: unknown;
};

function computeDiff(
  oldRecord: Record<string, unknown>,
  newRecord: Record<string, unknown>
): FieldDiff[] {
  const allKeys = new Set([
    ...Object.keys(oldRecord),
    ...Object.keys(newRecord),
  ]);
  const diffs: FieldDiff[] = [];
  for (const field of allKeys) {
    const oldVal = oldRecord[field];
    const newVal = newRecord[field];
    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      diffs.push({ field, oldValue: oldVal, newValue: newVal });
    }
  }
  return diffs;
}

function formatValue(val: unknown): string {
  if (val === null) return "null";
  if (val === undefined) return "undefined";
  if (typeof val === "string") return val;
  return JSON.stringify(val);
}

function DiffView({
  oldRecord,
  newRecord,
  bgcolor,
}: {
  oldRecord: Record<string, unknown>;
  newRecord: Record<string, unknown>;
  bgcolor: string;
}) {
  const diffs = computeDiff(oldRecord, newRecord);
  if (diffs.length === 0) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontStyle: "italic" }}
      >
        No fields changed
      </Typography>
    );
  }
  return (
    <Box component="pre" sx={{ ...jsonPreStyles, bgcolor, fontSize: "0.7rem" }}>
      {diffs
        .map(
          (d) =>
            `${d.field}:\n  - ${formatValue(d.oldValue)}\n  + ${formatValue(
              d.newValue
            )}`
        )
        .join("\n\n")}
    </Box>
  );
}

function extractReferencedIds(
  record: Record<string, unknown>,
  excludeId: string
): { field: string; uuid: string }[] {
  const refs: { field: string; uuid: string }[] = [];
  for (const [field, value] of Object.entries(record)) {
    if (
      typeof value === "string" &&
      isValidUuid(value) &&
      value !== excludeId
    ) {
      refs.push({ field, uuid: value });
    }
  }
  return refs;
}

const INITIAL_AUDIT_SIZE = 5;
const LOAD_MORE_SIZE = 10;

function ResultsView({
  result,
  onNavigate,
  onLookupId,
}: {
  result: LookupResult;
  onNavigate: () => void;
  onLookupId: (uuid: string) => void;
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isAtPermalink = location.pathname === `/staff/id/${result.id}`;
  const links = getNavigationLinks(result);
  const [jsonExpanded, setJsonExpanded] = useState(true);
  const hasRecord = result.record != null;
  const initialEntries = result.auditEntries ?? [];
  const [extraEntries, setExtraEntries] = useState<typeof initialEntries>([]);
  const [hasMore, setHasMore] = useState(
    initialEntries.length >= INITIAL_AUDIT_SIZE
  );
  const auditEntries = [...initialEntries, ...extraEntries];
  const lookupErrors = result.errors ?? [];
  const referencedIds = useMemo(
    () =>
      hasRecord
        ? extractReferencedIds(
            result.record as Record<string, unknown>,
            result.id
          )
        : [],
    [hasRecord, result.record, result.id]
  );

  const [loadMore, { loading: loadingMore }] = useLazyQuery(
    LOAD_MORE_AUDIT_ENTRIES,
    { fetchPolicy: "network-only" }
  );

  const handleLoadMore = useCallback(async () => {
    const { data } = await loadMore({
      variables: { uuid: result.id, offset: auditEntries.length },
    });
    const more = data?.loadMoreAuditEntries ?? [];
    if (more.length < LOAD_MORE_SIZE) {
      setHasMore(false);
    }
    if (more.length > 0) {
      setExtraEntries((prev) => [...prev, ...more]);
    }
  }, [loadMore, result.id, auditEntries.length]);

  const handleCopyJson = useCallback(() => {
    if (result.record) {
      navigator.clipboard.writeText(JSON.stringify(result.record, null, 2));
    }
  }, [result.record]);

  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Chip label={result.tableName} color="primary" size="small" />
          {!hasRecord && (
            <Chip
              label="Deleted"
              color="error"
              size="small"
              variant="outlined"
            />
          )}
          {result.recordName && (
            <Typography variant="body2" fontWeight={600}>
              {result.recordName}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {result.id}
          </Typography>
          <Tooltip title="Copy permalink">
            <IconButton
              onClick={() => {
                navigator.clipboard.writeText(
                  `${window.location.origin}/staff/id/${result.id}`
                );
              }}
            >
              <LinkIcon />
            </IconButton>
          </Tooltip>
          {!isAtPermalink && (
            <Tooltip title="Open permalink">
              <IconButton
                component={Link}
                to={`/staff/id/${result.id}`}
                target="_blank"
              >
                <OpenInNew />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {(result.tenantName || result.facilityName) && (
          <Box sx={{ display: "flex", gap: 2 }}>
            {result.tenantName && (
              <Typography variant="caption" color="text.secondary">
                Tenant: {result.tenantName}
              </Typography>
            )}
            {result.facilityName && (
              <Typography variant="caption" color="text.secondary">
                Facility: {result.facilityName}
              </Typography>
            )}
          </Box>
        )}
      </Stack>

      {lookupErrors.length > 0 && (
        <Stack spacing={0.5}>
          {lookupErrors.map((err, idx) => (
            <Typography
              key={idx}
              variant="body2"
              color="error"
              sx={{ fontSize: "0.8rem", fontFamily: "monospace" }}
            >
              {err}
            </Typography>
          ))}
        </Stack>
      )}

      {links.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Navigation
          </Typography>
          <Stack spacing={0.5}>
            {links.map((link, idx) => (
              <Box
                key={`${idx}-${link.path}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 0.5,
                  px: 1,
                  borderRadius: 1,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <OpenInNew sx={{ fontSize: 16, color: "text.secondary" }} />
                <MuiLink
                  component={Link}
                  to={link.path}
                  underline="hover"
                  variant="body2"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    onNavigate();
                    navigate(link.path);
                  }}
                >
                  {link.label}
                </MuiLink>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: "auto" }}
                >
                  {link.path}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {referencedIds.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Referenced IDs
          </Typography>
          <Stack spacing={0.5}>
            {referencedIds.map(({ field, uuid }) => (
              <Box
                key={`${field}-${uuid}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 0.5,
                  px: 1,
                  borderRadius: 1,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                onClick={() => onLookupId(uuid)}
              >
                <Search sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flexShrink: 0 }}
                >
                  {field}:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                >
                  {uuid}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      <Divider />

      {hasRecord && (
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <MuiLink
              component="button"
              variant="subtitle2"
              underline="hover"
              onClick={() => setJsonExpanded((v) => !v)}
              sx={{
                cursor: "pointer",
                border: "none",
                background: "none",
                p: 0,
              }}
            >
              {jsonExpanded ? "Hide" : "Show"} database record
            </MuiLink>
            <IconButton size="small" onClick={handleCopyJson} title="Copy JSON">
              <ContentCopy sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          {jsonExpanded && (
            <JsonBlock data={result.record} bgcolor={theme.palette.grey[100]} />
          )}
        </Box>
      )}

      {!hasRecord && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: "italic" }}
        >
          This record has been deleted. Audit history may contain its last known
          state.
        </Typography>
      )}

      {auditEntries.length > 0 && (
        <Box>
          <Accordion
            defaultExpanded={!hasRecord}
            disableGutters
            elevation={0}
            sx={{ "&:before": { display: "none" } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                px: 0,
                minHeight: 0,
                "& .MuiAccordionSummary-content": { my: 0.5 },
              }}
            >
              <Typography variant="subtitle2">
                Audit history ({auditEntries.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0 }}>
              <Stack spacing={1.5}>
                {auditEntries.map((entry, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Chip
                        label={entry.operation}
                        color={OPERATION_COLORS[entry.operation] ?? "default"}
                        size="small"
                        variant="outlined"
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(entry.timestamp)}
                      </Typography>
                      {entry.userEmail && (
                        <Typography variant="caption" color="text.secondary">
                          by {entry.userEmail}
                        </Typography>
                      )}
                    </Box>
                    {entry.operation === "UPDATE" &&
                    entry.record &&
                    entry.oldRecord ? (
                      <Stack spacing={1}>
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Changes:
                          </Typography>
                          <DiffView
                            oldRecord={
                              entry.oldRecord as Record<string, unknown>
                            }
                            newRecord={entry.record as Record<string, unknown>}
                            bgcolor={theme.palette.grey[50]}
                          />
                        </Box>
                        <Accordion
                          disableGutters
                          elevation={0}
                          sx={{
                            "&:before": { display: "none" },
                            bgcolor: "transparent",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMore sx={{ fontSize: 14 }} />}
                            sx={{
                              px: 0,
                              minHeight: 0,
                              "& .MuiAccordionSummary-content": { my: 0 },
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Full records
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 0, pt: 0 }}>
                            <Stack spacing={1}>
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontWeight: 600 }}
                                >
                                  New record:
                                </Typography>
                                <JsonBlock
                                  data={entry.record}
                                  bgcolor={theme.palette.grey[50]}
                                />
                              </Box>
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontWeight: 600 }}
                                >
                                  Old record:
                                </Typography>
                                <JsonBlock
                                  data={entry.oldRecord}
                                  bgcolor={theme.palette.grey[50]}
                                />
                              </Box>
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      </Stack>
                    ) : (
                      <>
                        {entry.record && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              Record:
                            </Typography>
                            <JsonBlock
                              data={entry.record}
                              bgcolor={theme.palette.grey[50]}
                            />
                          </Box>
                        )}
                        {entry.oldRecord && (
                          <Box sx={{ mt: entry.record ? 1 : 0 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              Previous record:
                            </Typography>
                            <JsonBlock
                              data={entry.oldRecord}
                              bgcolor={theme.palette.grey[50]}
                            />
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                ))}
              </Stack>
              <Tooltip
                title={hasMore ? "" : "No more audit entries"}
                placement="top"
              >
                <span>
                  <Button
                    size="small"
                    onClick={handleLoadMore}
                    disabled={loadingMore || !hasMore}
                    sx={{ mt: 1, alignSelf: "center" }}
                  >
                    {loadingMore ? "Loading..." : "Load more"}
                  </Button>
                </span>
              </Tooltip>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Stack>
  );
}

export type GotoUuidHandle = {
  openWithValue: (value: string) => void;
};

export const GotoUuidCommand = React.forwardRef<GotoUuidHandle>(
  function GotoUuidCommand(_, ref): React.ReactElement | null {
    const navigate = useNavigate();
    const location = useLocation();
    const isOnIdRoute = location.pathname.startsWith("/staff/id/");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [idInput, setIdInput] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<LookupResult | null>(null);
    const [history, setHistory] = useState<HistoryEntry[]>([]);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const submittingRef = useRef(false);
    const resultRef = useRef<LookupResult | null>(null);
    resultRef.current = result;

    const [lookupRecord, { loading }] = useLazyQuery(LOOKUP_RECORD_BY_UUID, {
      fetchPolicy: "network-only",
    });

    const handleOpen = useCallback((initialValue?: string) => {
      // Skip if already showing results for this ID (prevents re-open
      // when the route param updates after a successful lookup)
      if (initialValue && resultRef.current?.id === initialValue) return;
      setIdInput(initialValue ?? "");
      setError(null);
      setResult(null);
      setHistory(getSearchHistory());
      setDialogOpen(true);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        openWithValue: (value: string) => handleOpen(value),
      }),
      [handleOpen]
    );

    const handleClose = useCallback(() => {
      setDialogOpen(false);
      setIdInput("");
      setError(null);
      setResult(null);
    }, []);

    const handleSubmit = useCallback(async () => {
      const trimmed = idInput.trim();
      if (!trimmed) return;
      if (submittingRef.current) return;
      submittingRef.current = true;

      setError(null);
      setResult(null);

      try {
        const { data } = await lookupRecord({ variables: { uuid: trimmed } });
        const lookupResult = data?.lookupRecordByUuid;

        if (!lookupResult) {
          setError("No record found for this ID");
          return;
        }

        setResult(lookupResult);

        if (isOnIdRoute) {
          const isSameId = location.pathname === `/staff/id/${lookupResult.id}`;
          navigate(`/staff/id/${lookupResult.id}`, { replace: isSameId });
        }

        saveToSearchHistory({
          id: lookupResult.id,
          tableName: lookupResult.tableName,
          recordName: lookupResult.recordName ?? null,
          tenantName: lookupResult.tenantName ?? null,
          facilityName: lookupResult.facilityName ?? null,
          searchedAt: Date.now(),
        });
      } finally {
        submittingRef.current = false;
      }
    }, [idInput, lookupRecord, isOnIdRoute, navigate, location.pathname]);

    // Auto-submit when a valid UUID is entered, or any ID when on the /staff/id/ route
    useEffect(() => {
      const shouldAutoSubmit =
        isValidUuid(idInput.trim()) ||
        (isOnIdRoute && idInput.trim().length > 0);
      if (dialogOpen && shouldAutoSubmit && !loading && !result && !error) {
        handleSubmit();
      }
    }, [idInput, dialogOpen, loading, result, error, handleSubmit, isOnIdRoute]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !loading && !result) {
          e.preventDefault();
          handleSubmit();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          handleClose();
        }
      },
      [handleSubmit, handleClose, loading, result]
    );

    const handleReset = useCallback(() => {
      setResult(null);
      setError(null);
      setIdInput("");
      setHistory(getSearchHistory());
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }, []);

    useEffect(() => {
      if (dialogOpen && !result) {
        const id = window.setTimeout(() => inputRef.current?.focus(), 0);
        return () => window.clearTimeout(id);
      }
    }, [dialogOpen, result]);

    const commands = useMemo<Command[]>(
      () => [
        {
          id: "goto-id",
          label: "Goto ID",
          description: "Look up any record by ID and navigate to it",
          run: handleOpen,
          signature: "goto-id",
        },
      ],
      [handleOpen]
    );

    useRegisterCommands(commands, [commands], "goto-id");

    const showingResults = result !== null;

    return (
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        title="Goto ID"
        fullWidth
        maxWidth="md"
        noPrimaryAction={showingResults}
        hideCancelButton={showingResults}
        primaryActionButtonProps={
          showingResults
            ? undefined
            : {
                saveText: "Go",
                loading,
                disabled: loading || !idInput.trim(),
                hideStartIcon: true,
                onClick: handleSubmit,
              }
        }
        secondaryActionElements={
          showingResults
            ? [
                <Button key="reset" onClick={handleReset} size="small">
                  Look up another
                </Button>,
              ]
            : undefined
        }
      >
        {!showingResults && (
          <Stack spacing={2}>
            <Autocomplete<HistoryEntry, false, false, true>
              freeSolo
              options={history}
              inputValue={idInput}
              onInputChange={(_, value, reason) => {
                if (reason === "input" || reason === "clear") {
                  setIdInput(value);
                  setError(null);
                }
              }}
              onChange={(_, value) => {
                if (value && typeof value !== "string") {
                  setIdInput(value.id);
                  setError(null);
                }
              }}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.id
              }
              filterOptions={(options) => options}
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              renderOption={({ key: _, ...rest }, option) => (
                <li key={option.id} {...rest}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                      py: 0.25,
                    }}
                  >
                    <Chip
                      label={option.tableName}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{
                        flexShrink: 0,
                        minWidth: 120,
                        justifyContent: "center",
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {option.recordName ?? option.id}
                      </Typography>
                      {(option.tenantName || option.facilityName) && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                        >
                          {[option.tenantName, option.facilityName]
                            .filter(Boolean)
                            .join(" / ")}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={inputRef}
                  autoFocus
                  fullWidth
                  size="small"
                  placeholder="Paste a UUID or ID..."
                  onKeyDown={handleKeyDown}
                  error={!!error}
                  helperText={error}
                />
              )}
              disabled={loading}
            />
            {loading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Searching...
                </Typography>
              </Box>
            )}
          </Stack>
        )}
        {showingResults && (
          <ResultsView
            result={result}
            onNavigate={handleClose}
            onLookupId={handleOpen}
          />
        )}
      </Dialog>
    );
  }
);
